import { FormikProps } from 'formik/';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import StreetInput, { StreetInputProps } from './StreetInput';
import { FormikTextField } from './FormikTextField';

type RequiredFormikValues = {
  address: {
    street?: string;
    postalCode?: string;
  };
};
type FormikStreetInputProps<T extends RequiredFormikValues> = Pick<
  StreetInputProps,
  'label' | 'countryISO' | 'style'
> & {
  formik: FormikProps<T>;
  autoComplete: boolean;
};

export const FormikStreetInput = <T extends RequiredFormikValues>({
  formik,
  autoComplete,
  ...props
}: FormikStreetInputProps<T>) => {
  const { t } = useTranslation();
  // The type of Formik is incorrect which causes Typescript to not narrow the type
  const { values, errors, touched, setFieldTouched, setFieldValue } =
    formik as unknown as FormikProps<RequiredFormikValues>;

  const validationMessage = useMemo(() => {
    const isTouched = touched.address?.street || false;
    const errorMessage = errors.address?.street || '';
    if (isTouched && errorMessage) {
      return t(errorMessage);
    }
    return undefined;
  }, [touched.address?.street, errors.address?.street, t]);

  const onChange = useCallback(
    (value: string) => {
      setFieldValue('address.street', value);
    },
    [setFieldValue]
  );

  const onBlur = useCallback(() => {
    if (!touched.address?.street) {
      setFieldTouched('address.street', true);
    }
  }, [touched.address?.street, setFieldTouched]);

  return (
    <>
      {autoComplete && (
        <StreetInput
          {...props}
          value={values.address.street || ''}
          postalCode={values.address.postalCode || ''}
          onBlur={onBlur}
          onChange={onChange}
          validationMessage={validationMessage}
        />
      )}
      {!autoComplete && (
        <FormikTextField
          label={props.label}
          style={props.style}
          id="address.street"
          name="address.street"
          formikProps={formik}
          data-cy="addressStreet"
        />
      )}
    </>
  );
};
